import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { ProjectMeta, ProjectPayload, ProjectRecord } from '../types'

interface AppDB extends DBSchema {
  meta: {
    key: string
    value: ProjectMeta
    indexes: { 'by-updated': number }
  }
  payload: {
    key: string
    value: Blob
  }
}

const DB_NAME = 'pcba-global-view'
const DB_VERSION = 1
const LAST_KEY = 'pcba-global-view.last'

let dbPromise: Promise<IDBPDatabase<AppDB>> | null = null

function db(): Promise<IDBPDatabase<AppDB>> {
  dbPromise ??= openDB<AppDB>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      const meta = database.createObjectStore('meta', { keyPath: 'id' })
      meta.createIndex('by-updated', 'updatedAt')
      database.createObjectStore('payload')
    },
  })
  return dbPromise
}

async function pack(payload: ProjectPayload): Promise<Blob> {
  const text = JSON.stringify(payload)
  if ('CompressionStream' in window) {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'))
    return new Blob([await new Response(stream).arrayBuffer()], { type: 'application/gzip' })
  }
  return new Blob([text], { type: 'application/json' })
}

async function unpack(blob: Blob): Promise<ProjectPayload> {
  if (blob.type === 'application/gzip') {
    const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'))
    return JSON.parse(await new Response(stream).text()) as ProjectPayload
  }
  return JSON.parse(await blob.text()) as ProjectPayload
}

export async function listProjects(): Promise<ProjectMeta[]> {
  const database = await db()
  const all = await database.getAllFromIndex('meta', 'by-updated')
  return all.reverse()
}

export async function getProject(id: string): Promise<ProjectRecord | null> {
  const database = await db()
  const [meta, blob] = await Promise.all([database.get('meta', id), database.get('payload', id)])
  if (!meta || !blob) return null
  const payload = await unpack(blob)
  return { ...meta, payload }
}

export async function saveProject(record: ProjectRecord): Promise<void> {
  const database = await db()
  const blob = await pack(record.payload)
  const tx = database.transaction(['meta', 'payload'], 'readwrite')
  const { payload: _payload, ...meta } = record
  await Promise.all([tx.objectStore('meta').put(meta), tx.objectStore('payload').put(blob, record.id), tx.done])
}

export async function deleteProject(id: string): Promise<void> {
  const database = await db()
  const tx = database.transaction(['meta', 'payload'], 'readwrite')
  await Promise.all([tx.objectStore('meta').delete(id), tx.objectStore('payload').delete(id), tx.done])
}

export function rememberLast(id: string | null): void {
  try {
    if (id) localStorage.setItem(LAST_KEY, id)
    else localStorage.removeItem(LAST_KEY)
  } catch {
    // Storage can be blocked; the app still works for this session.
  }
}

export function lastProjectId(): string | null {
  try {
    return localStorage.getItem(LAST_KEY)
  } catch {
    return null
  }
}

export function newId(): string {
  if (crypto.randomUUID) return crypto.randomUUID()
  return `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}
